import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  SparklesIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import apiService from '../services/api';
import {
  NubieAiContact,
  NubieAiDirection,
  NubieAiResultatMessage,
  NubieAiResultatPublication,
  NubieAiSiteDetail,
  NubieAiSiteResume,
  NubieAiSocle,
  NubieAiEtatEnLigne,
  NubieAiQuestion,
  NubieAiTour,
} from '../types/api';
import { useOrganisation } from '../contexts/OrganisationContext';
import Logger from '../utils/logger';

import DirectionCard from '../components/NubieAI/DirectionCard';
import PhotosPanel from '../components/NubieAI/PhotosPanel';
import QuestionsPanel from '../components/NubieAI/QuestionsPanel';
import { useApercusMedias, substituerApercus } from '../components/NubieAI/useApercusMedias';

/**
 * NubieAI — le parcours jusqu'au choix du style.
 *
 * CE QUI MARCHE SANS AUCUNE CLE DE FOURNISSEUR, et c'est deliberement toute cette page :
 * creer un site, decrire son activite, saisir ses coordonnees, choisir parmi les quatre
 * styles. L'ecran des styles est de la lecture pure du registre du socle. L'atelier
 * conversationnel, lui, exige un modele et le dira clairement.
 *
 * Les coordonnees sont SAISIES et jamais generees : un numero plausible mais faux ne se
 * detecte pas avant qu'un appel se perde. C'est pour ca que le formulaire les demande
 * explicitement et que le texte d'aide le dit au client.
 */

/**
 * `useTranslation` avec un type volontairement simple.
 *
 * `i18next.d.ts` declare deja `resources: any` « pour eviter que TS tente d'inferer
 * toutes les clefs de chaque namespace » : le typage des clefs n'est donc pas garanti
 * dans ce depot, et ce fichier n'y renonce a rien. Ce qu'il evite, c'est que
 * l'inference generique de react-i18next fasse deborder la limite de recursion de
 * TypeScript (TS2589) -- et caster la FONCTION plutot que son resultat est ce qui
 * court-circuite l'instanciation au site d'appel.
 */
const traduire = useTranslation as unknown as (
  ns: string
) => { t: (cle: string, options?: Record<string, unknown>) => string };

/**
 * DEUX ETAPES, ET C'ETAIT QUATRE : `liste | brief | styles | atelier`.
 *
 * Demande de l'utilisateur -- « il y a trop d'etapes : formulaire, le template, et puis
 * on doit encore faire le prompt, alors que sur Lovable tu arrives sur la page d'accueil
 * et tu commences a prompter ». On arrive donc, on decrit, et la page se construit.
 *
 * LE STYLE EST PASSE DANS L'ATELIER, et ce n'est pas qu'un deplacement : le choisir
 * avant qu'aucune page n'existe, c'etait choisir A L'AVEUGLE. A cote de l'apercu, les
 * quatre directions se JUGENT -- et en changer ne coute aucune generation, puisque les
 * jetons sont ajoutes a la copie du socle du site.
 */
type Etape = 'accueil' | 'atelier';

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * L'horodatage d'un tour de discussion : date ET heure.
 *
 * Distinct de `formatDate`, qui ne rend que la date -- il sert aux fiches de site, ou
 * l'heure n'apprend rien. Ici elle compte : plusieurs echanges ont lieu le meme jour, et
 * le fil n'est conserve que quelques jours. Sans date, la disparition d'un echange plus
 * ancien que la retention ressemble a une perte.
 */
const formatHorodatage = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/* LA FENETRE DU CACHE DU PROXY DES SITES, en millisecondes.
 *
 * Doit suivre `cache.validSeconds` du chart `nubie-sites` (nubi-gitops) -- 300 s
 * depuis le 2026-11-01. Une valeur qui diverge ferait annoncer une duree fausse :
 * trop courte, le client croirait a une panne ; trop longue, il attendrait pour
 * rien. Il n'y a pas de source commune, donc c'est un couple a tenir a la main. */
const FENETRE_CACHE_MS = 300_000;

const CHAMPS_CONTACT = ['whatsapp', 'telephone', 'email', 'adresse'] as const;

/**
 * Retire les champs vides et rogne les espaces.
 *
 * Ecrit explicitement plutot qu'avec `Object.fromEntries(Object.entries(...))` : cette
 * tournure declenchait TS2589 (« type instantiation excessively deep ») en se combinant
 * a l'inference de react-i18next dans ce fichier. La version explicite est de surcroit
 * plus claire, et elle rogne les espaces -- ce que l'autre ne faisait pas.
 *
 * Un champ vide ne part PAS : cote API, « absent » veut dire inchange, pas vide.
 */
const contactNettoye = (c: NubieAiContact): NubieAiContact => {
  const sortie: NubieAiContact = {};
  CHAMPS_CONTACT.forEach((cle) => {
    const valeur = c[cle];
    if (valeur && valeur.trim()) sortie[cle] = valeur.trim();
  });
  return sortie;
};

/**
 * Construit le document AUTONOME que l'iframe affichera.
 *
 * TROIS SUBSTITUTIONS, ET CHACUNE REPOND A UNE PANNE PRECISE :
 *
 * 1. `/_nubie/base.css` et `/_nubie/motion.js` sont remplaces par leur contenu. Ces
 *    chemins ne seront servis qu'au site PUBLIE (lot 4) : les laisser ferait afficher
 *    une page nue, et le client conclurait que NubieAI a mal travaille alors que c'est
 *    l'apercu qui mentirait.
 * 2. Les jetons de la direction choisie sont injectes APRES le socle, donc ils gagnent.
 *    Sans eux l'apercu montrerait la palette par defaut quelle que soit la direction
 *    retenue -- exactement le defaut que les gardes du cote serveur empechent le modele
 *    de produire ; ce serait absurde de le reintroduire ici.
 * 3. Les emplacements de photo reçoivent une etiquette visible : un cadre vide se lit
 *    comme un trou, pas comme une place reservee.
 */
const documentApercu = (
  html: string,
  socle: NubieAiSocle | null,
  jetons: Record<string, string> | undefined
): string => {
  if (!socle) return html;
  const variables = Object.entries(jetons || {})
    .map(([cle, valeur]) => `  ${cle}: ${valeur};`)
    .join('\n');
  const style =
    `<style>\n${socle.base_css}\n:root {\n${variables}\n}\n` +
    /* `:empty` ET NON `.nubie-emplacement` tout court : un cadre REMPLI contient
       desormais une <img>, et l'etiquette se serait affichee PAR-DESSUS la photo du
       client. `:empty` ne matche qu'un element sans enfant, ce qui est exactement la
       definition d'un cadre en attente. */
    /* `position:relative` sur le cadre : le socle ne le pose pas, donc l'etiquette
       `absolute` se calait sur le document et non sur son cadre. Pose ICI et non dans
       le socle -- cette etiquette n'existe que dans l'apercu, le site publie n'en a
       pas. */
    '.nubie-emplacement{position:relative}' +
    '.nubie-emplacement:empty::after{content:"photo";position:absolute;inset:auto 0 0.5rem 0;' +
    'text-align:center;font-size:0.7rem;letter-spacing:0.1em;text-transform:uppercase;' +
    'opacity:0.5}\n</style>';
  return html
    .replace(/<link[^>]*href="\/_nubie\/base\.css"[^>]*>/g, style)
    .replace(/<script[^>]*src="\/_nubie\/motion\.js"[^>]*><\/script>/g,
             `<script>\n${socle.motion_js}\n</script>`);
};

const NubieAIPage: React.FC = () => {
  const { t } = traduire('nubieai');
  const { currentOrganisation } = useOrganisation();

  const [etape, setEtape] = useState<Etape>('accueil');
  const [chargement, setChargement] = useState(true);
  const [sites, setSites] = useState<NubieAiSiteResume[]>([]);
  const [directions, setDirections] = useState<NubieAiDirection[]>([]);
  const [siteCourant, setSiteCourant] = useState<NubieAiSiteDetail | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);

  const [socle, setSocle] = useState<NubieAiSocle | null>(null);

  /* LES APERCUS DES PHOTOS, AU NIVEAU DE LA PAGE ET NON DANS L'APERCU.
   *
   * Appele inconditionnellement et avant tout retour : les regles des crochets
   * interdisent un appel dans une branche. Une liste vide ne telecharge rien.
   *
   * Partage avec `PhotosPanel` par le meme crochet -- sinon chaque photo passerait deux
   * fois sur le reseau. */
  const apercusMedias = useApercusMedias(siteCourant?.id || '', siteCourant?.medias || []);
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [dernier, setDernier] = useState<NubieAiResultatMessage | null>(null);
  /* LE FIL DE DISCUSSION, lu depuis le serveur (V063).
   *
   * Avant, cette zone n'affichait que `dernier` -- le dernier resultat. Un rechargement
   * de page laissait donc un cadre vide, et c'etait le reproche d'origine : « le cadre
   * de prompt dans la console nubie ne garde pas le fil de discussion ». `dernier` est
   * CONSERVE, mais pour ce que le fil ne porte pas : les fichiers touches, le quota et
   * le plafond d'iterations. */
  const [fil, setFil] = useState<NubieAiTour[]>([]);
  /* LES QUESTIONS QUE LE MODELE ATTEND DE VOIR VALIDEES.
   *
   * Non vide veut dire « il n'a RIEN construit, il attend des reponses » -- l'ecran doit
   * alors montrer le formulaire et non un compte rendu : demander n'est pas echouer.
   * Signale par l'utilisateur : « saisir ces infos c'est fastidieux pour les clients, il
   * faudrait des propositions avec bouton pour valider ». */
  const [questions, setQuestions] = useState<NubieAiQuestion[]>([]);
  /* L'ETAT REEL DU SITE SUR LE WEB.
   *
   * Signale par l'utilisateur apres avoir publie : « il faut quelque chose cote console
   * pour nous dire si le site est en ligne ou pas, apres publication il repond pas de
   * suite ». `statut: 'publie'` est vrai du TELEVERSEMENT ; le certificat est demande
   * par site en HTTP-01 et met de trente a quatre-vingt-dix secondes. Pendant cette
   * fenetre le visiteur ne voit pas un 404 mais un AVERTISSEMENT DE SECURITE du
   * navigateur -- la pire premiere impression possible, et l'ecran disait « publie ». */
  const [enLigneEtat, setEnLigneEtat] = useState<NubieAiEtatEnLigne | null>(null);
  const [publication, setPublication] = useState<NubieAiResultatPublication | null>(null);
  const [publieEnCours, setPublieEnCours] = useState(false);
  const [supprimeEnCours, setSupprimeEnCours] = useState(false);

  const [nom, setNom] = useState('');
  const [brief, setBrief] = useState('');
  const [contact, setContact] = useState<NubieAiContact>({});

  const orgId = currentOrganisation?.id;

  const charger = useCallback(async () => {
    if (!orgId) return;
    setChargement(true);
    try {
      // Les deux en parallele : les styles ne dependent pas de l'organisation, et les
      // attendre en serie ferait clignoter l'ecran pour rien.
      const [liste, dirs] = await Promise.all([
        apiService.getNubieAiSites(orgId),
        apiService.getNubieAiDirections(),
      ]);
      setSites(liste);
      setDirections(dirs);
    } catch (err) {
      Logger.error('NubieAI: chargement impossible', err);
      toast.error(String(t('errors.generic')));
    } finally {
      setChargement(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    charger();
  }, [charger]);

  /**
   * Le fil de discussion du site.
   *
   * ECHOUE OUVERT, comme cote serveur : un fil illisible ne doit pas empecher de
   * travailler. Le client retombe alors sur le comportement d'avant V063 -- un cadre
   * vide, qui fonctionnait -- au lieu d'un ecran en erreur.
   *
   * CE N'EST PAS UN « ETAT VIDE » MAL AFFICHE : on ne remplace le fil qu'en cas de
   * succes, donc une panne passagere ne fait pas disparaitre ce qui est deja a l'ecran.
   * C'est la lecon de `BuildConfigList`, ou toute panne de lecture s'affichait comme
   * « aucune configuration » et invitait a en creer un doublon.
   */
  const chargerFil = useCallback(async (siteId: string) => {
    try {
      setFil(await apiService.getNubieAiMessages(siteId));
    } catch (err) {
      Logger.error('NubieAI: fil de discussion illisible', err);
    }
  }, []);


  /**
   * Le socle, charge UNE fois.
   *
   * Il ne depend ni du site ni de l'organisation : le recharger a chaque ouverture
   * serait une requete pour rien. Son absence ne rend l'apercu que moins beau, jamais
   * absent -- on n'empeche donc pas de travailler.
   */
  const assurerSocle = useCallback(async () => {
    if (socle) return;
    try {
      setSocle(await apiService.getNubieAiSocle());
    } catch (err) {
      Logger.error('NubieAI: socle indisponible, apercu sans style', err);
    }
  }, [socle]);

  /**
   * Entre dans l'atelier d'un site, en remettant a zero ce qui appartient au precedent.
   *
   * LES DEUX REMISES A ZERO NE SONT PAS DECORATIVES : sans `setFil` et `setDernier`, le
   * fil et le dernier resultat du site precedent resteraient a l'ecran sous le nom d'un
   * autre commerce -- un historique attribue au mauvais client. Les trois lignes
   * au-dessus, elles, CHARGENT : elles garnissent la section des informations.
   *
   * Le fil et le socle partent SANS `await` l'un sur l'autre : le socle n'est qu'une
   * question d'apparence de l'apercu, le fil est ce que le client vient relire.
   */
  const entrerDansAtelier = async (site: NubieAiSiteDetail) => {
    setSiteCourant(site);
    setNom(site.nom);
    setBrief(site.brief || '');
    setContact(site.contact || {});
    setDernier(null);
    setFil([]);
    setQuestions([]);
    setEnLigneEtat(null);
    setEtape('atelier');
    chargerFil(site.id);
    assurerSocle();
  };

  /**
   * Cree le site et lance la premiere generation, d'un seul geste.
   *
   * C'EST TOUT L'OBJET DE LA REFONTE : avant, il fallait remplir un formulaire, choisir
   * un style sur un deuxieme ecran, puis prompter sur un troisieme.
   *
   * LE TEXTE EST A LA FOIS LE BRIEF ET LE PREMIER MESSAGE, et les deux sont voulus. Le
   * brief est renvoye dans le CONTEXTE a chaque tour -- c'est la description de
   * l'activite, elle ne doit pas s'oublier ; le premier message est un vrai tour du
   * client, donc il appartient au fil. Oui, le texte part deux fois au premier tour :
   * quelques centaines d'octets contre ~29 000 jetons d'entree mesures sur une vraie
   * generation.
   *
   * LA DIRECTION EST POSEE EXPLICITEMENT, et c'est la seule chose a ne pas « simplifier
   * ». Sans elle, le prompt systeme retombe sur la premiere du registre : le client
   * verrait un style qu'il n'a pas demande, sans erreur. On pose donc la premiere
   * direction du registre -- la colonne est RENSEIGNEE, l'atelier la montre selectionnee,
   * et le client en change en un clic. La deduire du registre plutot que d'ecrire un
   * slug en dur evite qu'elle derive le jour ou le registre change.
   */
  const creerEtLancer = async () => {
    // `directions` NON VIDE EST UNE CONDITION, pas une commodite. Son chargement peut
    // avoir echoue -- `charger()` se contente alors d'un message -- et creer sans
    // direction ferait retomber le prompt systeme sur la premiere du registre : le
    // client verrait un style qu'il n'a pas demande, sans aucune erreur. Ne pas savoir
    // doit s'abstenir.
    if (!orgId || !nom.trim() || !brief.trim() || !directions.length || enregistrement) return;
    const demande = brief.trim();
    setEnregistrement(true);
    try {
      const site = await apiService.createNubieAiSite(orgId, {
        nom: nom.trim(),
        brief: demande,
        direction: directions[0]?.slug,
      });
      await entrerDansAtelier(site);
      await charger();
      await envoyerTexte(site.id, demande);
    } catch (err) {
      Logger.error('NubieAI: creation impossible', err);
      toast.error(String(t('errors.generic')));
    } finally {
      setEnregistrement(false);
    }
  };

  /**
   * Revient a l'accueil, formulaire VIERGE.
   *
   * LES QUATRE REMISES A ZERO SONT LA RAISON D'ETRE DE CETTE FONCTION, et pas un
   * nettoyage de politesse. Le formulaire d'accueil lit `nom` et `brief` : les laisser
   * garnis du site qu'on vient de quitter inviterait le client a creer un DOUBLON de son
   * propre site -- il verrait son nom deja ecrit et croirait le reprendre.
   *
   * Appelee aux deux sorties de l'atelier : le bouton de retour, et la suppression --
   * garder la fiche affichee apres une suppression montrerait un site qui n'existe plus,
   * et le moindre geste dessus rendrait un 404.
   */
  const revenirAccueil = () => {
    setSiteCourant(null);
    setNom('');
    setBrief('');
    setContact({});
    setFil([]);
    setDernier(null);
    setQuestions([]);
    setEnLigneEtat(null);
    setPublication(null);
    setEtape('accueil');
  };

  const choisirStyle = async (slug: string) => {
    if (!siteCourant) return;
    try {
      const site = await apiService.updateNubieAiSite(siteCourant.id, { direction: slug });
      setSiteCourant(site);
      await charger();
    } catch (err) {
      Logger.error('NubieAI: choix du style impossible', err);
      toast.error(String(t('errors.generic')));
    }
  };

  /**
   * Enregistre le nom, le brief et les coordonnees.
   *
   * UN NOM VIDE NE DOIT PAS EFFACER LE NOM : il est obligatoire cote API et c'est le
   * titre du site. Le repli sur la valeur actuelle est la garde -- envoyer la chaine
   * vide le viderait, l'API traitant « present et vide » comme « vide », a dessein.
   *
   * LES COORDONNEES PARTENT EN REMPLACEMENT COMPLET, et c'est la bonne semantique :
   * l'API remplace tout le bloc quand il est present. Un champ efface a l'ecran est
   * donc efface en base -- ce que le client vient de faire est ce qui est conserve.
   */
  const enregistrerInfos = async () => {
    if (!siteCourant || enregistrement) return;
    setEnregistrement(true);
    try {
      const site = await apiService.updateNubieAiSite(siteCourant.id, {
        nom: nom.trim() || siteCourant.nom,
        brief: brief.trim(),
        contact: contactNettoye(contact),
      });
      setSiteCourant(site);
      await charger();
      toast.success(String(t('infos.enregistre')));
    } catch (err) {
      Logger.error('NubieAI: enregistrement des informations impossible', err);
      toast.error(String(t('errors.generic')));
    } finally {
      setEnregistrement(false);
    }
  };

  const ouvrir = async (id: string) => {
    try {
      // DIRECTEMENT DANS L'ATELIER, et plus par l'ecran des styles : c'est l'apercu que
      // le client vient revoir, et le style se change maintenant a cote de lui.
      await entrerDansAtelier(await apiService.getNubieAiSite(id));
    } catch (err) {
      Logger.error('NubieAI: ouverture impossible', err);
      toast.error(String(t('errors.generic')));
    }
  };


  /**
   * Un tour de generation.
   *
   * LES CODES SONT TRAITES UN PAR UN, et pas par un message unique : « quota atteint »,
   * « une generation est deja en cours » et « la generation n'est pas activee » appellent
   * trois gestes differents de la part du client. Les confondre sous un seul texte lui
   * ferait reessayer indefiniment un refus definitif.
   *
   * Et la fiche est RELUE apres coup plutot que reconstruite depuis la reponse : c'est
   * elle qui porte les fichiers, et la reponse ne rend que les chemins touches.
   */
  const envoyerTexte = async (siteId: string, demande: string) => {
    setEnvoi(true);
    try {
      const resultat = await apiService.sendNubieAiMessage(siteId, demande);
      setDernier(resultat);
      setMessage('');
      /* REMPLACE A CHAQUE TOUR, jamais accumule : un jeu de questions appartient au tour
       * qui l'a pose. Les garder affichees apres une reponse ferait revalider des
       * propositions deja traitees -- et le modele les reposerait. */
      setQuestions(resultat.questions || []);
      setSiteCourant(await apiService.getNubieAiSite(siteId));
      /* LE FIL EST RELU, ET AUCUNE BULLE N'EST POSEE PAR AVANCE.
       *
       * Une bulle optimiste affirmerait « ceci est enregistre » avant que ce le soit --
       * et sur un refus de quota (402), un bail deja tenu (409) ou une generation non
       * activee (503), l'endpoint refuse AVANT d'ecrire quoi que ce soit. La bulle
       * serait donc un fait faux affiche comme un fait. Pendant la generation -- qui
       * dure des minutes -- le client voit ses propres mots dans le cadre, qui est
       * seulement desactive : rien n'est perdu, et rien n'est affirme.
       *
       * ET ON RELIT PLUTOT QUE D'AJOUTER `resultat.texte` : le serveur conserve le
       * texte REQUALIFIE -- « Je n'ai rien pu changer » quand toutes les ecritures ont
       * ete refusees -- donc relire garde une seule source de verite. L'appel est
       * negligeable a cote de la fiche, relue juste au-dessus. */
      await chargerFil(siteId);
      if (resultat.plafond_atteint) toast(String(t('atelier.iterationCap')));
    } catch (err) {
      const statut = (err as { response?: { status?: number } })?.response?.status;
      Logger.error('NubieAI: generation impossible', err);
      if (statut === 402) toast.error(String(t('errors.quotaReached')));
      else if (statut === 409) toast.error(String(t('errors.busy')));
      else if (statut === 503) toast.error(String(t('errors.notEnabled')));
      else toast.error(String(t('errors.generic')));
    } finally {
      setEnvoi(false);
    }
  };

  const envoyer = async () => {
    if (!siteCourant || !message.trim() || envoi) return;
    await envoyerTexte(siteCourant.id, message.trim());
  };

  /**
   * Met le site en ligne.
   *
   * LES REFUS SONT TRAITES UN PAR UN, et pas sous un message unique : 422 veut dire
   * qu'il manque quelque chose au site (style, page d'accueil, taille), 409 que
   * l'adresse est prise, 503 que la plateforme n'est pas prete, 502 que l'envoi a
   * echoue mais que le travail est conserve. Quatre gestes differents du client ;
   * les confondre lui ferait reessayer indefiniment un refus definitif.
   *
   * L'API renvoie DEJA un message ecrit pour l'ecran sur les refus metier (422, 409) :
   * on l'affiche tel quel plutot que de le recrire, pour ne pas avoir deux textes a
   * garder en phase.
   */
  /* LA SONDE, ET POURQUOI C'EST UN `useEffect` ET NON UNE BOUCLE DANS `publier`.
   *
   * Une boucle lancee dans le gestionnaire continuerait de tourner apres que le client
   * a quitte l'atelier -- elle interrogerait l'API toutes les cinq secondes pour un
   * ecran que personne ne regarde. Le nettoyage de l'effet l'arrete, et c'est la seule
   * facon fiable de le garantir.
   *
   * ELLE S'ARRETE AUSSI SUR UN ETAT TERMINAL : `en_ligne` (c'est fini) et
   * `certificat_absent` (ca ne se resoudra pas tout seul, il faut republier). Seul
   * `certificat_en_cours` justifie d'attendre, et `inconnu` d'insister un peu.
   *
   * PLAFONNEE A TRENTE TOURS, soit deux minutes et demie. Au-dela, insister n'apprend
   * plus rien : on laisse le dernier etat connu a l'ecran plutot que de sonder sans fin.
   */
  useEffect(() => {
    if (etape !== 'atelier' || !siteCourant || siteCourant.statut !== 'publie') return;
    let vivant = true;
    let tours = 0;
    let minuterie: ReturnType<typeof setTimeout> | undefined;

    const sonder = async () => {
      if (!vivant) return;
      try {
        const e = await apiService.getNubieAiEtatEnLigne(siteCourant.id);
        if (!vivant) return;
        setEnLigneEtat(e);
        if (e.etat === 'en_ligne' || e.etat === 'certificat_absent') return;
      } catch (err) {
        /* NE PAS EFFACER L'ETAT CONNU sur une panne de lecture : une panne passagere
         * ferait disparaitre l'information de l'ecran. C'est la lecon de
         * `BuildConfigList`, ou toute panne s'affichait comme « aucune configuration ». */
        Logger.error('NubieAI: etat en ligne illisible', err);
      }
      tours += 1;
      if (vivant && tours < 30) minuterie = setTimeout(sonder, 5000);
    };

    sonder();
    return () => {
      vivant = false;
      if (minuterie) clearTimeout(minuterie);
    };
  }, [etape, siteCourant?.id, siteCourant?.statut]); // eslint-disable-line react-hooks/exhaustive-deps

  /* L'INSTANT DE LA DERNIERE REPUBLICATION, pour avertir que la page met quelques
   * minutes a se propager. Un horodatage et non un booleen : il faut savoir COMBIEN
   * de temps s'est ecoule, et l'avis doit disparaitre de lui-meme. */
  const [republieLe, setRepublieLe] = useState<number | null>(null);

  /* L'AVIS S'EFFACE SEUL. Sans ce minuteur il resterait affiche indefiniment et
   * finirait par mentir -- la page etant propagee depuis longtemps. Le `clearTimeout`
   * du nettoyage compte : ce composant se demonte quand on quitte l'atelier. */
  useEffect(() => {
    if (republieLe === null) return;
    const reste = FENETRE_CACHE_MS - (Date.now() - republieLe);
    if (reste <= 0) {
      setRepublieLe(null);
      return;
    }
    const minuterie = setTimeout(() => setRepublieLe(null), reste);
    return () => clearTimeout(minuterie);
  }, [republieLe]);

  const publier = async () => {
    if (!siteCourant || publieEnCours) return;
    /* RELEVE AVANT L'APPEL, et c'est ce qui rend l'avis juste. Une PREMIERE
     * publication est visible tout de suite : le proxy n'a rien en cache, et il ne
     * garde une absence que 10 s (`notFoundSeconds`). C'est la REpublication qui
     * attend, parce que le proxy sert encore sa copie de la page precedente pendant
     * `cache.validSeconds` -- passe a 300 s le 2026-11-01 pour borner le cout des
     * telechargements B2.
     *
     * Annoncer « cinq minutes » a quelqu'un qui publie pour la premiere fois lui
     * ferait douter d'une fonctionnalite qui marche. */
    const etaitPublie = siteCourant.statut === 'publie';
    setPublieEnCours(true);
    try {
      const r = await apiService.publishNubieAiSite(siteCourant.id);
      if (etaitPublie) setRepublieLe(Date.now());
      setPublication(r);
      /* REMIS A ZERO POUR QUE LA SONDE REPARTE. Une republication peut tomber sur un
       * certificat deja emis -- le socle garde le secret TLS a la depublication, et
       * republier sous 90 jours ne redemande rien -- mais on ne le suppose pas. */
      setEnLigneEtat(null);
      setSiteCourant(await apiService.getNubieAiSite(siteCourant.id));
      await charger();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const statut = e?.response?.status;
      const detail = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : null;
      Logger.error('NubieAI: publication impossible', err);
      if (statut === 422 || statut === 409) {
        toast.error(detail || String(t('errors.publicationEchouee')));
      } else if (statut === 503) {
        toast.error(String(t('errors.publicationIndisponible')));
      } else {
        toast.error(String(t('errors.publicationEchouee')));
      }
    } finally {
      setPublieEnCours(false);
    }
  };

  const depublier = async () => {
    if (!siteCourant || publieEnCours) return;
    // `window.confirm` et non un modal maison : retirer un site de la ligne est
    // reversible et garde l'adresse, donc ca ne justifie pas un ecran dedie.
    if (!window.confirm(String(t('publication.confirmerDepublier')))) return;
    setPublieEnCours(true);
    try {
      await apiService.unpublishNubieAiSite(siteCourant.id);
      setPublication(null);
      setSiteCourant(await apiService.getNubieAiSite(siteCourant.id));
      await charger();
      toast.success(String(t('publication.retire')));
    } catch (err) {
      Logger.error('NubieAI: depublication impossible', err);
      toast.error(String(t('errors.generic')));
    } finally {
      setPublieEnCours(false);
    }
  };

  /**
   * Supprime DEFINITIVEMENT le site. Rien ne se rattrape.
   *
   * POURQUOI LE MESSAGE ENUMERE CE QUI DISPARAIT. La doctrine de cette console est
   * ecrite dans `DeploymentManagementPage` : un `window.confirm` suffit, SAUF quand le
   * geste detruit des donnees irrecuperables. Ici il detruit la page, les ORIGINAUX des
   * photos -- ceux que la depublication garde a dessein -- et libere l'adresse. Le
   * message doit donc les nommer, et il nomme le NOMBRE de photos : « 3 photos » porte
   * ce qu'un avertissement generique ne porte pas.
   *
   * Un modal dedie serait disproportionne : la comparaison juste n'est pas la
   * suppression d'un deploiement, qui emporte une base de production, mais celle d'un
   * bucket NubiS3 -- titre plus avertissement, meme patron.
   */
  const supprimer = async () => {
    if (!siteCourant || supprimeEnCours) return;

    const photos = (siteCourant.medias ?? []).length;
    // Recalcule ici : le `enLigne` du rendu est derive plus bas dans le composant, donc
    // hors de portee. Meme expression, et c'est la meme source de verite -- le statut et
    // l'adresse du site, pas un etat local.
    const estEnLigne = siteCourant.statut === 'publie' && !!siteCourant.url_publique;
    const avertissement = [
      String(t('suppression.confirmer', { nom: siteCourant.nom })),
      '',
      String(t('suppression.ceQuiDisparait')),
      photos > 0 ? String(t('suppression.photos', { count: photos })) : null,
      estEnLigne ? String(t('suppression.adresse')) : null,
    ]
      .filter(Boolean)
      .join('\n');

    if (!window.confirm(avertissement)) return;

    setSupprimeEnCours(true);
    try {
      await apiService.deleteNubieAiSite(siteCourant.id);
      // L'ecran revient a un formulaire vierge : garder la fiche affichee montrerait un
      // site qui n'existe plus, et le moindre geste dessus rendrait un 404.
      revenirAccueil();
      await charger();
      toast.success(String(t('suppression.faite')));
    } catch (err) {
      const e = err as { response?: { status?: number } };
      Logger.error('NubieAI: suppression impossible', err);
      // UN 502 EST UN CAS A PART, et son message doit le dire : le retrait du site EN
      // LIGNE n'a pas abouti, donc le site existe toujours et reste supprimable. Un
      // message generique laisserait croire a une perte.
      toast.error(
        String(e?.response?.status === 502 ? t('suppression.enLigneEchoue') : t('errors.generic'))
      );
    } finally {
      setSupprimeEnCours(false);
    }
  };


  /**
   * Un champ de coordonnees.
   *
   * Vivait dans le bloc de l'ecran « brief », qui n'existe plus. Remonte ici plutot que
   * recopie dans l'atelier : les quatre champs partagent leur mise en forme, et deux
   * copies divergeraient.
   */
  const champ = (
    cle: keyof NubieAiContact,
    libelle: string,
    aide?: string,
    type = 'text'
  ) => (
    <div key={cle}>
      <label className="block text-sm font-medium text-text-primary mb-1">{libelle}</label>
      <input
        type={type}
        value={contact[cle] || ''}
        onChange={(e) => setContact({ ...contact, [cle]: e.target.value })}
        className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent"
      />
      {aide && <p className="text-xs text-text-tertiary mt-1">{aide}</p>}
    </div>
  );

  // ── En-tete commun ──────────────────────────────────────────────────────── //

  const EnTete = (
    <div className="mb-8">
      <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
        <SparklesIcon className="w-6 h-6 text-accent" strokeWidth={1.8} />
        {t('title')}
      </h1>
      <p className="text-sm text-text-secondary mt-1">{t('subtitle')}</p>
    </div>
  );

  if (chargement) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        {EnTete}
        <div className="flex items-center justify-center py-20 text-text-secondary">
          {t('atelier.sending')}
        </div>
      </div>
    );
  }

  // ── Etape : l'atelier ───────────────────────────────────────────────────── //

  if (etape === 'atelier' && siteCourant) {
    /* L'APERCU MONTRE LES PHOTOS, et il a fallu le corriger : l'injection se fait a la
     * PUBLICATION -- c'est ce qui protege les photos d'une regeneration -- donc le
     * brouillon porte des cadres VIDES et le client ne voyait ses images qu'apres avoir
     * publie.
     *
     * `apercu_html` est rendu par l'API, par la MEME fonction que la publication, avec
     * un `src` vers sa route d'apercu. On ne fait ici que substituer les URL d'objet :
     * refaire l'injection donnerait deux implementations qui divergeraient.
     *
     * `null` quand aucune photo n'est placee -- le cas le plus courant -- et on retombe
     * alors sur le brouillon tel quel. */
    const brouillon = siteCourant.fichiers?.['index.html'];
    const page = siteCourant.apercu_html
      ? substituerApercus(siteCourant.apercu_html, siteCourant.medias || [], apercusMedias)
      : brouillon;
    const jetons = directions.find((d) => d.slug === siteCourant.direction)?.jetons;
    const enLigne = siteCourant.statut === 'publie' && !!siteCourant.url_publique;
    // L'ADRESSE VIENT DE L'API, jamais reconstruite ici : le domaine est un reglage de
    // plateforme, et le recopier en ferait une valeur a garder en phase avec la
    // configuration. La reponse de publication est preferee a la fiche parce qu'elle
    // est plus fraiche -- la fiche a ete lue avant l'envoi.
    const adressePublique = publication?.url || siteCourant.url_publique || '';
    const illimite = dernier ? dernier.plafond_messages < 0 : false;

    return (
      <div className="p-6 max-w-6xl mx-auto">
        <button
          type="button"
          onClick={revenirAccueil}
          className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" strokeWidth={1.8} />
          {siteCourant.nom}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* L'APERCU PREND LA PLUS GRANDE PART : c'est le resultat, la conversation
              n'est que le moyen.

              MAIS IL PASSE EN SECOND SUR ECRAN ETROIT. En dessous de `lg` la grille
              tombe a une colonne : laisser l'apercu en premier mettait le champ de
              saisie SOUS un cadre de 32rem, donc hors de l'ecran. Un formulaire qu'il
              faut aller chercher en defilant derriere un cadre vide n'existe pas. */}
          <div className="order-2 lg:order-none lg:col-span-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium text-text-primary">{t('atelier.preview')}</h2>
              {siteCourant.socle_version && (
                <span className="text-[11px] text-text-tertiary">
                  socle {siteCourant.socle_version}
                </span>
              )}
            </div>
            {page ? (
              <iframe
                title={t('atelier.preview')}
                srcDoc={documentApercu(page, socle, jetons)}
                /* `allow-scripts` SEUL : motion.js doit tourner, et sans
                   `allow-same-origin` le document reste dans une origine opaque --
                   il ne peut donc ni lire le stockage de la console ni l'atteindre. */
                sandbox="allow-scripts"
                className="w-full h-[32rem] bg-white border border-border-1 rounded-card"
              />
            ) : (
              /* Court a dessein : un cadre vide de 32rem n'affiche rien et repousse
                 tout le reste. Il grandira quand il aura une page a montrer. */
              <div className="w-full h-48 lg:h-[32rem] flex items-center justify-center text-center text-sm text-text-secondary bg-surface-1 border border-border-1 border-dashed rounded-card px-8">
                {t('atelier.noPreview')}
              </div>
            )}
          </div>

          {/* La conversation */}
          <div className="order-1 lg:order-none lg:col-span-2 flex flex-col gap-4">
            {/* LE FIL, ET NON LE SEUL DERNIER RESULTAT.
                C'etait le reproche d'origine : « le cadre de prompt dans la console
                nubie ne garde pas le fil de discussion ». Il est lu depuis le serveur
                (V063), donc il survit a un rechargement de page et a un retour par
                l'ecran des styles. */}
            {fil.length > 0 && (
              <div className="flex flex-col gap-3 max-h-[30rem] overflow-y-auto pr-1">
                {fil.map((tour, i) => (
                  <div
                    key={`${tour.cree_le || 'local'}-${i}`}
                    className={
                      tour.role === 'user'
                        ? 'self-end max-w-[85%] bg-surface-2 border border-border-1 rounded-card px-3 py-2'
                        : 'self-start max-w-[95%] bg-surface-1 border border-border-1 rounded-card px-3 py-2'
                    }
                  >
                    <p className="text-[11px] uppercase tracking-wide text-text-quaternary mb-1">
                      {tour.role === 'user' ? t('atelier.vous') : t('atelier.nubie')}
                      {tour.cree_le ? ` · ${formatHorodatage(tour.cree_le)}` : ''}
                    </p>
                    <p className="text-sm text-text-primary whitespace-pre-wrap">
                      {tour.contenu}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* CE QUE LE FIL NE PORTE PAS, et c'est pour ca que `dernier` reste :
                il ne garde que LES MOTS -- ni fichiers touches, ni quota. Ces trois
                valeurs ne concernent que le dernier tour, et les rattacher a une bulle
                les ferait croire vraies de tout l'historique. */}
            {dernier && (
              <div className="border-t border-border-1 pt-3">
                {dernier.fichiers_touches.length > 0 && (
                  <p className="text-xs text-text-tertiary">
                    {t('atelier.filesTouched')} {dernier.fichiers_touches.join(', ')}
                  </p>
                )}
                <p className="mt-1 text-xs text-text-tertiary">
                  {illimite
                    ? t('atelier.quotaUnlimited')
                    : t('atelier.quota', {
                        used: dernier.messages_utilises,
                        total: dernier.plafond_messages,
                      })}
                </p>
              </div>
            )}

            {/* LE FORMULAIRE DE QUESTIONS, AU-DESSUS DU CADRE DE SAISIE.
                Au-dessus et non a la place : le client garde le droit de taper ce qu'il
                veut, et les deux chemins aboutissent au meme message. Le remplacer
                l'enfermerait dans les propositions -- or ce sont des suppositions. */}
            {questions.length > 0 && (
              <QuestionsPanel
                questions={questions}
                envoi={envoi}
                onRepondre={(reponse) => {
                  if (!siteCourant || !reponse.trim() || envoi) return;
                  envoyerTexte(siteCourant.id, reponse.trim());
                }}
              />
            )}

            <div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                disabled={envoi}
                placeholder={t('atelier.placeholder') as string}
                className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-sm text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent disabled:opacity-50 resize-none"
              />
              <button
                type="button"
                onClick={envoyer}
                disabled={envoi || !message.trim()}
                className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent text-white rounded-button text-sm font-medium hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {envoi ? (
                  t('atelier.sending')
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-4 h-4" strokeWidth={1.8} />
                    {t('atelier.send')}
                  </>
                )}
              </button>
            </div>

            {/* LES PHOTOS N'APPARAISSENT QU'UNE FOIS LA PAGE CONSTRUITE : c'est elle
                qui porte les emplacements, et sans emplacement il n'y a nulle part ou
                poser une photo. */}
            {page && siteCourant && (
              <PhotosPanel
                siteId={siteCourant.id}
                emplacements={siteCourant.emplacements ?? []}
                medias={siteCourant.medias ?? []}
                onChange={async () => {
                  setSiteCourant(await apiService.getNubieAiSite(siteCourant.id));
                }}
              />
            )}

            {/* LA MISE EN LIGNE N'APPARAIT QU'UNE FOIS LA PAGE CONSTRUITE : proposer
                de publier un site vide ne menerait qu'a un refus de l'API. */}
            {page && (
              <div className="border-t border-border-1 pt-4">
                <h3 className="text-sm font-medium text-text-primary mb-1">
                  {t('publication.titre')}
                </h3>

                {enLigne ? (
                  <>
                    {/* L'ETAT REEL, ET NON « publie ». Signale par l'utilisateur :
                        « apres publication il repond pas de suite ». `statut: publie`
                        est vrai du televersement ; le certificat met de trente a
                        quatre-vingt-dix secondes. */}
                    {enLigneEtat && enLigneEtat.etat !== 'en_ligne' ? (
                      <div className="rounded-card border border-border-1 bg-surface-2 px-3 py-2">
                        <p className="flex items-center gap-2 text-xs text-text-primary">
                          {enLigneEtat.etat === 'certificat_en_cours' && (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0"
                              aria-hidden="true"
                            />
                          )}
                          {/* LE MESSAGE VIENT DE L'API, affiche TEL QUEL. Le reformuler
                              ici donnerait deux textes a garder en phase, et c'est
                              l'ecran qui finirait par mentir. */}
                          {enLigneEtat.message}
                        </p>
                        {/* L'ADRESSE EN TEXTE, PAS EN LIEN, tant que ce n'est pas pret :
                            cliquer trop tot produit un avertissement de securite du
                            navigateur, parce que nginx sert son certificat par defaut
                            faute de secret TLS. Le client doit voir son adresse ; il ne
                            doit pas etre invite a l'ouvrir maintenant. */}
                        <p className="mt-1 text-sm text-text-tertiary break-all">
                          {adressePublique.replace(/^https?:\/\//, '')}
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-text-secondary">{t('publication.enLigne')}</p>
                        <a
                          href={adressePublique}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1.5 flex items-center gap-1.5 text-sm text-accent hover:underline break-all"
                        >
                          {adressePublique.replace(/^https?:\/\//, '')}
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.8} />
                        </a>
                      </>
                    )}
                    {/* L'AVIS DE PROPAGATION, et pourquoi il est ICI et non dans un
                        toast. Un toast disparait en quelques secondes : le commercant
                        qui recharge sa page deux minutes plus tard et retrouve
                        l'ancienne version ne l'aurait plus. C'est exactement le
                        scenario du 2026-10-08 -- deux photos publiees, une seule
                        visible, et rien ne l'expliquait.

                        Il ne s'affiche QUE sur une republication (voir `publier`), et
                        il s'efface seul au bout de la fenetre de cache. */}
                    {republieLe !== null && (
                      <p className="mt-2 text-xs text-text-tertiary">
                        {t('publication.propagation')}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={publier}
                        disabled={publieEnCours}
                        className="flex-1 px-3 py-2 bg-surface-2 text-text-primary border border-border-1 rounded-button text-sm hover:border-border-2 disabled:opacity-40 transition-colors"
                      >
                        {publieEnCours ? t('publication.encours') : t('publication.republier')}
                      </button>
                      <button
                        type="button"
                        onClick={depublier}
                        disabled={publieEnCours}
                        className="px-3 py-2 text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-40 transition-colors"
                      >
                        {t('publication.depublier')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-text-secondary">{t('publication.aide')}</p>
                    <button
                      type="button"
                      onClick={publier}
                      disabled={publieEnCours}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent text-white rounded-button text-sm font-medium hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <GlobeAltIcon className="w-4 h-4" strokeWidth={1.8} />
                      {publieEnCours ? t('publication.encours') : t('publication.bouton')}
                    </button>
                  </>
                )}

                {publication && (
                  <p className="mt-2 text-xs text-text-tertiary">
                    {t('publication.detail', {
                      fichiers: publication.fichiers,
                      kio: Math.ceil(publication.octets / 1024),
                    })}
                  </p>
                )}
              </div>
            )}

            {/* LA SUPPRESSION EST HORS DU BLOC `page &&`, ET C'EST DELIBERE. Un site
                cree puis jamais genere n'a pas de page -- donc pas de bloc de
                publication -- et c'est precisement le reste qu'on veut pouvoir
                retirer. L'enfermer dans cette garde aurait rendu ces sites-la
                indelebiles depuis la console. */}
            <div className="border-t border-border-1 pt-4">
              <button
                type="button"
                onClick={supprimer}
                disabled={supprimeEnCours || publieEnCours}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-text-tertiary border border-border-1 rounded-button hover:text-red-500 hover:border-red-500/40 disabled:opacity-40 transition-colors"
              >
                <TrashIcon className="w-4 h-4" strokeWidth={1.8} />
                {supprimeEnCours ? t('suppression.encours') : t('suppression.bouton')}
              </button>
              <p className="mt-1.5 text-xs text-text-quaternary">
                {t('suppression.aide')}
              </p>
            </div>
          </div>
        </div>

        {/* LE STYLE, A COTE DE L'APERCU ET NON AVANT LUI.
            Il etait un ECRAN, traverse avant qu'aucune page n'existe -- donc choisi a
            l'aveugle. Ici les quatre directions se jugent contre le resultat, et en
            changer ne coute AUCUNE generation : les jetons sont ajoutes a la copie du
            socle du site, la page n'est pas reecrite. */}
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-text-primary">{t('directions.title')}</h2>
          <p className="text-sm text-text-secondary mt-1 mb-5">{t('directions.intro')}</p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {directions.map((dir) => (
              <DirectionCard
                key={dir.slug}
                direction={dir}
                selected={siteCourant.direction === dir.slug}
                onSelect={choisirStyle}
                labelChosen={String(t('directions.chosen'))}
                labelSansPhoto={String(t('directions.sansPhoto'))}
                exempleNom={String(t('apercu.nom'))}
                exempleLieu={String(t('apercu.lieu'))}
                exempleAction={String(t('apercu.action'))}
              />
            ))}
          </div>
        </section>

        {/* LES INFORMATIONS DE L'ACTIVITE.
            Elles etaient saisies A LA CREATION et plus jamais modifiables -- un trou
            qui existait avant cette refonte : aucun ecran ne les rouvrait. Elles vivent
            ici parce que le brief repart dans le CONTEXTE a chaque tour : le corriger
            change ce que le modele sait, sans rien reecrire a la main. */}
        <section className="mt-10 bg-surface-1 border border-border-1 rounded-card p-6">
          <h2 className="text-lg font-semibold text-text-primary">{t('infos.titre')}</h2>
          <p className="text-sm text-text-secondary mt-1 mb-5">{t('infos.aide')}</p>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                {t('create.nom')}
              </label>
              <input
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                {t('create.brief')}
              </label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent resize-y"
              />
              <p className="text-xs text-text-tertiary mt-1">{t('create.briefHelp')}</p>
            </div>

            <div className="pt-2 border-t border-border-0">
              <h3 className="text-sm font-semibold text-text-primary mt-4">
                {t('create.contactTitle')}
              </h3>
              {/* Dire au client que rien n'est invente : c'est la garantie produit, et
                  elle doit se lire a l'endroit ou il saisit. */}
              <p className="text-xs text-text-tertiary mt-1 mb-4">{t('create.contactHelp')}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {champ('whatsapp', String(t('create.whatsapp')), String(t('create.whatsappHelp')))}
                {champ('telephone', String(t('create.telephone')), undefined, 'tel')}
                {champ('email', String(t('create.email')), undefined, 'email')}
                {champ('adresse', String(t('create.adresse')))}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={enregistrerInfos}
            disabled={enregistrement}
            className="mt-6 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-contrast rounded-button text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enregistrement ? t('infos.enregistrement') : t('infos.enregistrer')}
          </button>
        </section>
      </div>
    );
  }

  // ── Etape : l'accueil — on arrive, on decrit, la page se construit ─────── //

  /* CE CADRE EST L'ECRAN D'ENTREE, et c'est tout l'objet de la refonte.
   *
   * Avant : un formulaire (nom, brief, quatre coordonnees), puis un ecran de choix de
   * style, puis l'atelier ou il fallait ENCORE prompter. Trois ecrans avant la premiere
   * page. Maintenant : un nom, une description, et ca part.
   *
   * LE NOM RESTE UN CHAMP A PART, et le deduire du texte serait un mauvais service : il
   * devient l'ADRESSE publique du site. Se tromper obligerait a la changer, donc a
   * redemander un certificat -- et Let's Encrypt plafonne les certificats nouveaux par
   * domaine enregistre.
   *
   * LE RESTE EST DANS L'ATELIER : le style, parce qu'il ne se juge qu'a cote d'un
   * apercu ; les coordonnees, parce qu'elles n'entrent dans la page qu'une fois celle-ci
   * construite. */
  return (
    <div className="p-6 max-w-5xl mx-auto">
      {EnTete}

      <div className="bg-surface-1 border border-border-1 rounded-card p-6">
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('create.nom')}
            </label>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder={String(t('create.nomPlaceholder'))}
              disabled={enregistrement}
              className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent disabled:opacity-50"
            />
            <p className="text-xs text-text-tertiary mt-1">{t('create.nomHelp')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              {t('create.brief')}
            </label>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={6}
              placeholder={String(t('create.briefPlaceholder'))}
              disabled={enregistrement}
              className="w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent disabled:opacity-50 resize-y"
            />
            <p className="text-xs text-text-tertiary mt-1">{t('create.briefHelp')}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={creerEtLancer}
          disabled={!nom.trim() || !brief.trim() || !directions.length || enregistrement}
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-accent hover:bg-accent-hover text-accent-contrast rounded-button text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SparklesIcon className="w-4 h-4" strokeWidth={1.8} />
          {enregistrement ? t('accueil.lancerEnCours') : t('accueil.lancer')}
        </button>
        <p className="mt-2 text-xs text-text-quaternary">{t('accueil.aide')}</p>
      </div>

      {/* PAS D'ETAT VIDE SEPARE : le cadre ci-dessus EST l'invitation. Un grand bloc
          « creez votre premiere vitrine » au-dessus d'un formulaire deja ouvert ne
          faisait que repousser ce formulaire hors de l'ecran. */}
      {sites.length > 0 && (
        <div className="mt-10">
          <h2 className="text-sm font-medium text-text-secondary mb-3">
            {t('accueil.mesSites')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map((s) => (
              <button
                key={s.id}
                onClick={() => ouvrir(s.id)}
                className="text-left bg-surface-1 border border-border-1 rounded-card p-5 hover:border-border-2 hover:shadow-card-hover transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-text-primary truncate">{s.nom}</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-badge bg-surface-2 text-text-secondary flex-shrink-0">
                    {t(`statut.${s.statut}`)}
                  </span>
                </div>
                <p className="flex items-center gap-1.5 text-xs text-text-tertiary mt-3">
                  <DocumentTextIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {s.nb_fichiers > 0
                    ? t('list.files', { count: s.nb_fichiers })
                    : t('list.noFile')}
                </p>
                <p className="text-xs text-text-quaternary mt-1">
                  {t('list.modified', { date: formatDate(s.modifie_le) })}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NubieAIPage;
